/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eraser, MessageCircle, RotateCcw, Save, Scale, Target, Zap } from 'lucide-react';
import api from '../services/api';
import { fetchPortfolio } from '../store/slices/portfolioSlice';
import { exclusionKey } from '../services/analysisExclusions';
import { currentWeights, planRebalance } from '../services/rebalance';
import { useToast } from '../contexts/ToastContext';
import { useShell } from '../components/shell/ShellContext';
import { BrokerGate } from '../components/shell/Broker';
import { DepositPlanner } from '../components/plan/DepositPlanner';
import { BasketDialog } from '../components/trading/BasketDialog';
import { cx } from '../theme/cx';
import { formatMoney, formatNumber, formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, EmptyState, Input, Money, PageHeader, SkeletonTable, SymbolMark, Table, TBody, Td, Th, THead, Tr,
} from '../components/ui';

const parse = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const fromPlan = (plan) => ({
  targets: Object.fromEntries((plan?.targets || []).map((t) => [exclusionKey(t.symbol), String(t.target_pct)])),
  cash: plan ? String(plan.cash_target_pct ?? 0) : '0',
  minTrade: plan ? String(plan.min_trade_amount ?? 300) : '300',
});

const ORDER_TONES = { buy: ['info', 'Acheter'], sell: ['warn', 'Vendre'] };

const ordersMessage = (plan) => {
  const lines = plan.orders.map((o) => `- ${ORDER_TONES[o.action][1]} ${o.shares} × ${o.symbol} (~${formatMoney(o.tradeValue, { decimals: 0 })})`);
  return `Voici les ordres que suggère mon assistant de rééquilibrage :\n${lines.join('\n')}\nLiquidités après opérations : ${formatMoney(plan.cashAfter, { decimals: 0 })}.\nQu'en penses-tu ? Y a-t-il un ordre à éviter ou à étaler ?`;
};

const Rebalance = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { openChat } = useShell();
  const [basketOpen, setBasketOpen] = useState(false);
  const { summary, positions, saxoConnected, loading } = useSelector((state) => state.portfolio);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [savedVersion, setSavedVersion] = useState(0);

  useEffect(() => {
    if (saxoConnected && !summary) dispatch(fetchPortfolio());
  }, [dispatch, saxoConnected, summary]);

  useEffect(() => {
    let alive = true;
    api.get('/rebalance/targets')
      .then((res) => { if (alive) setDraft(fromPlan(res.data)); })
      .catch(() => { if (alive) { setDraft(fromPlan(null)); setLoadError('Impossible de lire l’allocation cible enregistrée.'); } });
    return () => { alive = false; };
  }, []);

  const total = summary?.total_value || 0;
  const cash = summary?.cash_balance || 0;
  const weights = useMemo(() => currentWeights(positions, total), [positions, total]);
  const sorted = useMemo(() => [...positions].sort((a, b) => (b.market_value || 0) - (a.market_value || 0)), [positions]);

  const plan = useMemo(() => {
    if (!draft || !summary) return null;
    const targets = {};
    Object.entries(draft.targets).forEach(([k, v]) => { const n = parse(v); if (n !== null) targets[k] = n; });
    return planRebalance({ positions, cash, targets, cashTargetPct: parse(draft.cash) || 0, minTrade: parse(draft.minTrade) ?? 300 });
  }, [draft, summary, positions, cash]);

  const targetSum = draft ? Object.values(draft.targets).reduce((s, v) => s + (parse(v) || 0), 0) + (parse(draft.cash) || 0) : 0;
  const balanced = Math.abs(targetSum - 100) <= 0.5;

  const setTarget = (key, value) => setDraft((d) => ({ ...d, targets: { ...d.targets, [key]: value } }));
  const fromCurrent = () => setDraft((d) => ({
    ...d,
    targets: Object.fromEntries(Object.entries(weights).map(([k, w]) => [k, String(Math.round(w * 10) / 10)])),
    cash: String(total > 0 ? Math.round((cash / total) * 1000) / 10 : 0),
  }));
  const clear = () => setDraft((d) => ({ ...d, targets: {} }));

  const save = async () => {
    setSaving(true);
    setError('');
    const targets = sorted
      .map((p) => ({ symbol: p.symbol, target_pct: parse(draft.targets[exclusionKey(p.symbol)]) }))
      .filter((t) => t.target_pct !== null);
    try {
      const res = await api.put('/rebalance/targets', { cash_target_pct: parse(draft.cash) || 0, min_trade_amount: parse(draft.minTrade) || 0, targets });
      setDraft(fromPlan(res.data));
      setSavedVersion((v) => v + 1);
      toast.success('Allocation cible enregistrée');
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible d’enregistrer l’allocation cible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Rééquilibrage"
        description="Fixez une allocation cible : les ordres suggérés respectent les contraintes du PEA (actions entières, pas de marge, réserve de liquidités)."
      />
      <BrokerGate>
        {(!draft || (loading && !summary)) && (
          <Card role="status"><span className="sr-only">Chargement…</span><SkeletonTable rows={6} cols={4} /></Card>
        )}
        {draft && summary && plan && (
          <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-5 xl:items-start">
            <Card className="xl:col-span-3">
              <CardHeader
                icon={Target}
                title="Allocation cible"
                description="Poids visé par ligne, en % de la valeur totale. Une ligne sans cible reste telle quelle."
                actions={(
                  <>
                    <Button size="sm" variant="secondary" leadingIcon={RotateCcw} onClick={fromCurrent}>Partir de l’actuelle</Button>
                    <Button size="sm" variant="ghost" leadingIcon={Eraser} onClick={clear}>Effacer</Button>
                  </>
                )}
              />
              {loadError && <Banner tone="warn" className="mb-4">{loadError}</Banner>}
              <Table>
                <THead>
                  <tr>
                    <Th>Valeur</Th>
                    <Th align="right">Actuel</Th>
                    <Th align="right">Cible</Th>
                    <Th align="right" className="hidden sm:table-cell">Écart</Th>
                  </tr>
                </THead>
                <TBody>
                  {sorted.map((p) => {
                    const key = exclusionKey(p.symbol);
                    const target = parse(draft.targets[key]);
                    const gap = target === null ? null : target - (weights[key] || 0);
                    return (
                      <Tr key={p.position_id || p.symbol}>
                        <Td className="w-full max-w-0">
                          <div className="flex items-center gap-3">
                            <SymbolMark symbol={p.symbol} size="sm" />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-fg">{p.symbol}</p>
                              <p className="truncate text-xs text-fg-subtle">{p.description}</p>
                            </div>
                          </div>
                        </Td>
                        <Td align="right" numeric>{formatPercent(weights[key], { sign: false })}</Td>
                        <Td align="right">
                          <Input
                            aria-label={`Cible pour ${p.symbol} (%)`}
                            inputMode="decimal"
                            className="ml-auto w-24"
                            inputClassName="h-9 pr-8 text-right num"
                            value={draft.targets[key] ?? ''}
                            placeholder="—"
                            onChange={(e) => setTarget(key, e.target.value)}
                            trailing={<span className="pr-1.5 text-xs text-fg-subtle">%</span>}
                          />
                        </Td>
                        <Td align="right" numeric className={cx('hidden whitespace-nowrap sm:table-cell', gap === null ? 'text-fg-subtle' : gap > 0 ? 'text-info-300' : gap < 0 ? 'text-warn-300' : 'text-fg-muted')}>
                          {gap === null ? 'hors plan' : `${formatNumber(gap, { decimals: 1, sign: true })} pt`}
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
              <div className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-fg-body">Réserve de liquidités</span>
                  <Input aria-label="Réserve de liquidités (%)" inputMode="decimal" inputClassName="text-right num pr-8" value={draft.cash} onChange={(e) => setDraft((d) => ({ ...d, cash: e.target.value }))} trailing={<span className="pr-1.5 text-xs text-fg-subtle">%</span>} />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-fg-body">Ordre minimal</span>
                  <Input aria-label="Ordre minimal (€)" inputMode="decimal" inputClassName="text-right num pr-8" value={draft.minTrade} onChange={(e) => setDraft((d) => ({ ...d, minTrade: e.target.value }))} trailing={<span className="pr-1.5 text-xs text-fg-subtle">€</span>} />
                </label>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <Badge tone={balanced ? 'gold' : 'warn'} size="md" data-testid="target-total">Total {formatNumber(targetSum, { decimals: 1 })} %</Badge>
                <Button leadingIcon={Save} loading={saving} onClick={save}>Enregistrer</Button>
              </div>
              {error && <Banner tone="loss" role="alert" className="mt-4">{error}</Banner>}
            </Card>

            <Card className="xl:col-span-2" data-testid="rebalance-orders">
              <CardHeader icon={Scale} title="Ordres suggérés" description={`Aux derniers cours connus, sur ${formatMoney(plan.total, { decimals: 0 })} au total.`} />
              {plan.warnings.map((w) => <Banner key={w} tone="warn" compact className="mb-3">{w}</Banner>)}
              {plan.orders.length === 0 ? (
                <EmptyState
                  className="py-8"
                  icon={Target}
                  title={Object.keys(draft.targets).some((k) => parse(draft.targets[k]) !== null) ? 'Aucun ordre nécessaire' : 'Définissez vos cibles'}
                  description={Object.keys(draft.targets).some((k) => parse(draft.targets[k]) !== null)
                    ? 'Les écarts restent sous l’ordre minimal ou les liquidités ne permettent pas d’acheter.'
                    : 'Saisissez un poids cible par ligne, ou partez de l’allocation actuelle.'}
                />
              ) : (
                <>
                  <ul className="divide-y divide-line">
                    {plan.orders.map((o) => {
                      const [tone, label] = ORDER_TONES[o.action];
                      return (
                        <li key={o.key} className="flex items-center gap-3 py-3 first:pt-0" data-testid="rebalance-order">
                          <Badge tone={tone} className="w-[4.5rem] justify-center">{label}</Badge>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-fg">{o.symbol}</p>
                            <p className="num text-xs text-fg-subtle">{formatNumber(o.shares)} × <Money value={o.price} sensitive={false} /></p>
                          </div>
                          <Money value={o.tradeValue} decimals={0} className="font-semibold text-fg" />
                        </li>
                      );
                    })}
                  </ul>
                  <dl className="mt-5 space-y-2 border-t border-line pt-4 text-sm">
                    <div className="flex justify-between"><dt className="text-fg-muted">Ventes</dt><dd><Money value={plan.sells} decimals={0} /></dd></div>
                    <div className="flex justify-between"><dt className="text-fg-muted">Achats</dt><dd><Money value={plan.buys} decimals={0} /></dd></div>
                    <div className="flex justify-between font-semibold"><dt className="text-fg">Liquidités après</dt><dd><Money value={plan.cashAfter} decimals={0} /> <span className="num font-normal text-fg-subtle">({formatPercent(plan.cashAfterPct, { sign: false })})</span></dd></div>
                  </dl>
                  <div className="mt-5 grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                    <Button fullWidth leadingIcon={Zap} onClick={() => setBasketOpen(true)} className="bg-trade-500 hover:bg-trade-400 active:bg-trade-600">Passer ces ordres</Button>
                    <Button variant="secondary" fullWidth leadingIcon={MessageCircle} onClick={() => openChat(ordersMessage(plan))}>En parler à Gladys</Button>
                  </div>
                  <BasketDialog
                    open={basketOpen}
                    onClose={() => setBasketOpen(false)}
                    origin="rebalance"
                    reason="Rééquilibrage vers vos poids cibles."
                    lines={plan.orders.map((o) => ({ symbol: o.symbol, side: o.action, shares: o.shares, price: o.price }))}
                  />
                </>
              )}
              <p className="mt-5 text-xs leading-relaxed text-fg-subtle">
                Rien ne part sans vous : « Passer ces ordres » ouvre un panier vérifié par les garde-fous du trading (ventes d’abord, achats ensuite). Actions entières, frais non inclus. Dans le PEA, une vente n’est pas imposée tant que rien n’est retiré du plan.
              </p>
            </Card>
          </div>
          <DepositPlanner version={savedVersion} />
          </div>
        )}
      </BrokerGate>
    </div>
  );
};

export default Rebalance;
